// The session endpoints on {sessionApiUrl}/auth/* (backend, public routes):
//
//   POST /auth/session  sign-in app only: hands the Cognito refresh token to
//                       the backend, which keeps it in an HttpOnly cookie on
//                       the parent domain
//   POST /auth/refresh  any app: a new ID token from that cookie
//   POST /auth/logout   clears the cookie (and revokes the refresh token)
//
// All of them degrade gracefully: when the endpoint is unreachable or says
// no, the caller does what the apps did before (redirect to sign-in).

import { getSessionConfig, ResolvedSessionConfig } from "./config";
import { cookies } from "./cookies";
import { isExpired } from "./token";

type Fetched = { ok: boolean; status: number; json: () => Promise<any> };

async function post(
	config: ResolvedSessionConfig,
	path: string,
	init: { headers?: Record<string, string>; body?: string; keepalive?: boolean } = {}
): Promise<Fetched> {
	const controller = typeof AbortController === "function" ? new AbortController() : null;
	const timer = controller ? setTimeout(() => controller.abort(), config.requestTimeoutMs) : null;
	try {
		return await fetch(`${config.sessionApiUrl}${path}`, {
			method: "POST",
			credentials: "include",
			headers: init.headers,
			body: init.body,
			keepalive: init.keepalive,
			signal: controller?.signal,
		});
	} finally {
		if (timer) clearTimeout(timer);
	}
}

/** What /auth/refresh and /auth/session answer. accessToken is null from an older backend. */
export type SessionTokens = {
	idToken: string;
	/** Cognito access token: keep it in memory only (never a cookie or storage) */
	accessToken: string | null;
	/** epoch seconds or ms as the backend sends it; null when absent */
	expiresAt: number | null;
};

const readTokens = async (response: Fetched): Promise<SessionTokens | null> => {
	try {
		const body = await response.json();
		const idToken = body && typeof body.idToken === "string" ? body.idToken : null;
		if (!idToken || isExpired(idToken, 0)) return null;
		return {
			idToken,
			accessToken: typeof body.accessToken === "string" && body.accessToken ? body.accessToken : null,
			expiresAt: typeof body.expiresAt === "number" ? body.expiresAt : null,
		};
	} catch {
		return null;
	}
};

let inFlight: Promise<SessionTokens | null> | null = null;

/**
 * New tokens from the HttpOnly refresh cookie (POST /auth/refresh): the ID
 * token is written to the ID token cookie; the access token is only returned
 * (for callers that need it in memory, e.g. Cognito account changes) and
 * never stored. Resolves null on any failure (no cookie, revoked, endpoint
 * unreachable, timeout). Concurrent calls (and refreshSession) share one
 * request.
 */
export function refreshSessionTokens(config: ResolvedSessionConfig = getSessionConfig()): Promise<SessionTokens | null> {
	if (inFlight) return inFlight;
	const request = (async () => {
		try {
			const response = await post(config, "/auth/refresh");
			if (!response.ok) return null;
			const tokens = await readTokens(response);
			if (tokens) cookies.setIdToken(tokens.idToken, config);
			return tokens;
		} catch (error) {
			console.warn(`[web-session:${config.app}] /auth/refresh unavailable`, (error as Error)?.message ?? error);
			return null;
		}
	})();
	inFlight = request;
	request.then(
		() => {
			if (inFlight === request) inFlight = null;
		},
		() => {
			if (inFlight === request) inFlight = null;
		}
	);
	return request;
}

/**
 * A new ID token from the HttpOnly refresh cookie (POST /auth/refresh),
 * written to the ID token cookie and returned; null on any failure.
 * Concurrent calls share one request.
 */
export function refreshSession(config: ResolvedSessionConfig = getSessionConfig()): Promise<string | null> {
	return refreshSessionTokens(config).then((tokens) => tokens?.idToken ?? null);
}

export type EstablishResult = { ok: boolean; idToken?: string; accessToken?: string | null; status?: number };

const established = new Map<string, Promise<EstablishResult>>();

/**
 * Sign-in app only: POST /auth/session with the Cognito refresh token (body)
 * and ID token (Authorization), so the backend sets the HttpOnly refresh
 * cookie. Never throws: a failure is logged and resolves { ok: false }.
 * Identical concurrent calls share one request; a success is remembered for
 * the page, so repeating it for the same refresh token is free.
 */
export function establishSession(
	tokens: { idToken: string; refreshToken?: string | null },
	config: ResolvedSessionConfig = getSessionConfig()
): Promise<EstablishResult> {
	const { idToken, refreshToken } = tokens;
	if (!idToken || !refreshToken) return Promise.resolve({ ok: false });
	const existing = established.get(refreshToken);
	if (existing) return existing;
	const request = (async (): Promise<EstablishResult> => {
		try {
			const response = await post(config, "/auth/session", {
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
				body: JSON.stringify({ refreshToken }),
			});
			if (!response.ok) {
				console.warn(`[web-session:${config.app}] /auth/session failed: ${response.status}`);
				return { ok: false, status: response.status };
			}
			const fresh = await readTokens(response);
			return { ok: true, status: response.status, idToken: fresh?.idToken, accessToken: fresh?.accessToken };
		} catch (error) {
			console.warn(`[web-session:${config.app}] /auth/session unavailable`, (error as Error)?.message ?? error);
			return { ok: false };
		}
	})();
	established.set(refreshToken, request);
	request.then((result) => {
		if (!result.ok && established.get(refreshToken) === request) established.delete(refreshToken);
	});
	return request;
}

/**
 * POST /auth/logout: clears the HttpOnly refresh cookie (backend revokes the
 * token). keepalive, so a navigation right after doesn't cancel it. Never
 * throws; resolves whether it reached the backend.
 */
export async function endServerSession(config: ResolvedSessionConfig = getSessionConfig()): Promise<boolean> {
	established.clear();
	try {
		const response = await post(config, "/auth/logout", { keepalive: true });
		return response.ok;
	} catch (error) {
		console.warn(`[web-session:${config.app}] /auth/logout unavailable`, (error as Error)?.message ?? error);
		return false;
	}
}

/** tests only */
export function resetSessionRequests(): void {
	inFlight = null;
	established.clear();
}
